import { ChevronRightIcon, Icon } from '@chakra-ui/icons'
import { Box, Flex, Td, Tr, Text, IconButton } from '@chakra-ui/react'
import React, { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  currentMeasurementTypeMap,
  getReadableValue,
  isTemperatureParam,
  measurementTypeMap,
} from '../../../../utils/helpers'
import { GoDotFill } from 'react-icons/go'
import {
  DeviceCurrentValues,
  useDeviceCurrentValues,
} from '../../../settings/hooks/useDeviceCurrentValues'

export const DesignValueBox = ({ children }: { children: ReactNode }) => (
  <Box as="span" fontFamily="Inter" fontWeight="700" fontSize="12px" color="#484848">
    {children}
  </Box>
)

export const DesignMeasurementUnitBox = ({ children }: { children: ReactNode }) => (
  <Box as="span" fontFamily="Inter" fontWeight="400" fontSize="12px" color="#9B9B9B">
    {children}
  </Box>
)

export const getUnmeasuredStats = (
  device: any,
  currentParameterTitle: string,
  totalValue: number,
  totalCost: number,
) => {
  if (!device.children?.length) {
    return { show: false, value: 0, cost: 0, current: 0 }
  }

  const sum = device.children.reduce(
    (acc: any, child: any) => {
      const p = child.parameters?.find(
        (x: any) => x.parameter_title.toLowerCase() === currentParameterTitle,
      )
      return {
        value: acc.value + (typeof p?.value === 'number' ? p.value : 0),
        cost: acc.cost + (typeof p?.total_cost === 'number' ? p.total_cost : 0),
      }
    },
    { value: 0, cost: 0 },
  )

  const value = totalValue - sum.value
  const cost = totalCost - sum.cost

  const percent = (value / (totalValue || 1)) * 100
  return { show: percent >= 1, value, cost }
}

export const renderUnmeasuredEnergyRow = (
  device: any,
  level: number,
  currentParameterTitle: string,
  totalValue: number,
  totalCost: number,
  currentValue?: number,
  currentValues?: Map<string, DeviceCurrentValues>,
) => {
  if (!device.children || device.children.length === 0) return null

  const sumChildValue = device.children.reduce((acc, child) => {
    const v = child.parameters?.find(
      (x: any) => x.parameter_title.toLowerCase() === currentParameterTitle,
    )?.value
    return acc + (typeof v === 'number' ? v : 0)
  }, 0)

  const sumChildCost = device.children.reduce((acc, child) => {
    const v = child.parameters?.find(
      (x: any) => x.parameter_title.toLowerCase() === currentParameterTitle,
    )?.total_cost
    return acc + (typeof v === 'number' ? v : 0)
  }, 0)

  const sumChildCurrent = device.children.reduce((acc, child) => {
    const staticCurrent = child.parameters?.find(
      (x: any) => x.parameter_title.toLowerCase() === currentParameterTitle,
    )?.current_value
    const liveCurrent = currentValues
      ? getValueFromMap(currentValues, child.physical_id, currentParameterTitle, staticCurrent)
      : staticCurrent
    return acc + (typeof liveCurrent === 'number' ? liveCurrent : 0)
  }, 0)

  const parentCurrent = currentValues
    ? getValueFromMap(currentValues, device.physical_id, currentParameterTitle, currentValue)
    : (currentValue ?? 0)

  const unmeasuredValue = totalValue - sumChildValue
  const unmeasuredCost = totalCost - sumChildCost
  const currentUnmeasuredValue = (parentCurrent ?? 0) - sumChildCurrent

  const percentDiff = Math.abs((unmeasuredValue / (totalValue || 1)) * 100)
  const currentPercentDiff = Math.abs((currentUnmeasuredValue / (currentValue || 1)) * 100)

  if (percentDiff < 1 && currentPercentDiff < 1) {
    return null
  }

  return (
    <Tr
      key={`unmeasured-${device.id}`}
      role="group"
      position="relative"
      bg="#F5F7FA"
      _hover={{ bg: '#F5F7FA' }}
      onClick={(e) => e.stopPropagation()}
    >
      <Td pl={0} minWidth="400px">
        <Flex align="center">
          <Box w="28px" display="flex" justifyContent="center" alignItems="center" mr={1}>
            <Box w="12px" />
          </Box>

          <Box ml={`${Math.max(0, level * 14)}px`} mr={2} alignSelf="stretch">
            <ChildConnector isLast />
          </Box>

          <Flex fontStyle="italic" align="center" gap={2} mt={3} pb={2} color="gray.600">
            <Text>Unmeasured energy</Text>
          </Flex>
        </Flex>
      </Td>

      <Td isNumeric>
        <DesignValueBox>{currentUnmeasuredValue.toFixed(2)}</DesignValueBox>{' '}
        <DesignMeasurementUnitBox>
          {getReadableValue(currentMeasurementTypeMap, currentParameterTitle)}
        </DesignMeasurementUnitBox>
      </Td>

      <Td isNumeric>
        <DesignValueBox>{unmeasuredValue.toFixed(2)}</DesignValueBox>{' '}
        <DesignMeasurementUnitBox>
          {getReadableValue(measurementTypeMap, currentParameterTitle)}
        </DesignMeasurementUnitBox>
      </Td>

      {!isTemperatureParam(currentParameterTitle) && (
        <Td isNumeric>
          <DesignValueBox>{unmeasuredCost.toFixed(2)}</DesignValueBox>{' '}
          <DesignMeasurementUnitBox>лв.</DesignMeasurementUnitBox>
        </Td>
      )}
    </Tr>
  )
}

export const ChildConnector = ({
  isLast = false,
  bleed = 30,
}: {
  isLast?: boolean
  bleed?: number
}) => {
  const yElbow = bleed + 12
  const yBottom = 24 + bleed * 2

  return (
    <Box position="relative" w="12px" mr={2} mt={5} alignSelf="stretch">
      <svg
        width="26"
        viewBox={`0 0 26 ${yBottom}`}
        preserveAspectRatio="none"
        style={{
          position: 'absolute',
          top: `-${bleed}px`,
          height: `calc(100% + ${bleed * 2}px)`,
          overflow: 'visible',
          pointerEvents: 'none',
        }}
      >
        <path
          d={`M8 0 V ${isLast ? yElbow : yBottom} M8 ${yElbow} H20`}
          stroke="#D0D5DD"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </Box>
  )
}

export const renderDeviceRowRecursive = (
  device: any,
  level: number,
  from: any,
  to: any,
  period: string,
  expandedIds: Set<string>,
  toggleExpand: (id: string) => void,
  selectedId: string | null,
  setSelectedId: (id: string) => void,
  currentParameterTitle: string,
  totalValue: number,
  totalCost: number,
  navigate: ReturnType<typeof useNavigate>,
  isOnline: boolean,
  currentValues: Map<string, DeviceCurrentValues>,
  parentExpanded: boolean = false,
  isLastChild: boolean = false,
) => {
  const isExpanded = expandedIds.has(String(device.id))
  const hasChildren = device.children?.length > 0
  const blockBg = parentExpanded || isExpanded ? '#F5F7FA' : 'transparent'

  const param = device.parameters?.find(
    (x: any) => x.parameter_title.toLowerCase() === currentParameterTitle,
  )

  const stats = getUnmeasuredStats(
    device,
    currentParameterTitle,
    totalValue,
    totalCost,
  )

  return (
    <React.Fragment key={device.id}>
      <Tr
        role="group"
        position="relative"
        bg={blockBg}
        _hover={{ bg: '#F5F7FA', cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation()

          device.children.length > 0 && toggleExpand(device.id)
        }}
      >
        <Td pl={0} minWidth="400px">
          <Flex align="center">
            <Box
              w="28px"
              display="flex"
              justifyContent="center"
              alignItems="center"
              mr={1}
            >
              {hasChildren ? (
                <Box
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleExpand(device.id)
                  }}
                  cursor="pointer"
                  transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
                  transition="transform 0.2s"
                  fontWeight="bold"
                  fontSize="10px"
                  ml={isExpanded ? 2 : 0}
                >
                  ▶
                </Box>
              ) : (
                <Box w="12px" />
              )}
            </Box>

            {level > 0 && (
              <Box ml={`${Math.max(0, (level - 1) * 14)}px`} mr={2} alignSelf="stretch">
                <ChildConnector isLast={isLastChild} />
              </Box>
            )}

            <Flex align="center" gap={2}>
              <Text>{device.title}</Text>
              <Icon as={GoDotFill} boxSize="24px" color={isOnline ? '#00d183' : 'red'} />
              <IconButton
                aria-label="Open details"
                icon={<ChevronRightIcon />}
                fontSize="20px"
                size="md"
                variant="ghost"
                color="gray.500"
                opacity={0}
                pointerEvents="none"
                transition="opacity 0.15s ease"
                _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
                _groupFocusWithin={{ opacity: 1, pointerEvents: 'auto' }}
                _hover={{ color: 'gray.700', bg: 'transparent' }}
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedId(device.id)
                  navigate(
                    `/overview/device/?device_id=${device.id}&title=${device.title}&from=${from}&to=${to}&period=${period}`,
                  )
                }}
              />
            </Flex>
          </Flex>
        </Td>

        <Td isNumeric>
          <DesignValueBox
            children={
              getValueFromMap(
                currentValues,
                device.physical_id,
                currentParameterTitle,
                param?.current_value,
              )?.toFixed(2) ?? 0
            }
          />{' '}
          <DesignMeasurementUnitBox
            children={getReadableValue(currentMeasurementTypeMap, currentParameterTitle)}
          />
        </Td>

        <Td isNumeric>
          <DesignValueBox children={(param?.value ?? 0).toFixed(2)} />{' '}
          <DesignMeasurementUnitBox
            children={getReadableValue(measurementTypeMap, currentParameterTitle)}
          />
        </Td>

        {!isTemperatureParam(currentParameterTitle) && (
          <Td isNumeric>
            <DesignValueBox children={(param?.total_cost ?? 0).toFixed(2)} />{' '}
            <DesignMeasurementUnitBox children={' лв.'} />
          </Td>
        )}
      </Tr>

      {hasChildren && isExpanded && (
        <>
          {device.children.map((child: any, idx: number) =>
            renderDeviceRowRecursive(
              child,
              level + 1,
              from,
              to,
              period,
              expandedIds,
              toggleExpand,
              selectedId,
              setSelectedId,
              currentParameterTitle,
              child.parameters?.find(
                (x: any) => x.parameter_title.toLowerCase() === currentParameterTitle,
              )?.value,
              child.parameters?.find(
                (x: any) => x.parameter_title.toLowerCase() === currentParameterTitle,
              )?.total_cost,
              navigate,
              child?.is_online,
              currentValues,
              true,
              idx === device.children.length - 1 && !stats.show,
            ),
          )}

          {renderUnmeasuredEnergyRow(
            device,
            level,
            currentParameterTitle,
            totalValue,
            totalCost,
            param?.current_value,
            currentValues,
          )}
        </>
      )}
    </React.Fragment>
  )
}

export function getValueFromMap(map, outerKey, valueKey, current_value) {
  const entry = map.get(outerKey)
  if (!entry || !entry.values || Object.keys(entry.values).length === 0) {
    return current_value ? current_value : 0
  }

  if (valueKey === 'totalconsumedenergy') {
    return entry.values['activepower'] / 1000
  } else if (valueKey === 'totalgeneratedenergy') {
    return entry.values['generatedpower'] / 1000
  } else if (valueKey === 'temperature') {
    return entry.values[valueKey]
  }

  return current_value ? current_value : 0
}

